import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Box,
    Card,
    CircularProgress,
    Container,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AccessTimeRounded,
    CancelRounded,
    CheckCircleRounded,
    HourglassEmptyRounded,
    ReplyRounded,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { Support_addMessage, Support_getById, Support_setMessagesRead } from "../../../network/api/support.api";
import { ErrorContext } from "../../../components/GympinPagesProvider";
import { SupportStatus } from "../../../helper/enums/SupportStatus";
import _SupportMessageItem from "./_SupportMessageItem";
import _SupportHeader from "./_SupportHeader";
import _SupportReplyBox from "./_SupportReplyBox";
import _SupportCloseDialog from "./_SupportCloseDialog";

const STATUS_CONFIG = {
    AWAITING_USER: {
        label: "پاسخ کارشناس (منتظر شما)",
        color: "#0284c7",
        bgColor: "rgba(2, 132, 199, 0.1)",
        borderColor: "rgba(2, 132, 199, 0.25)",
        icon: <ReplyRounded sx={{ fontSize: 16 }} />,
    },
    AWAITING_EXPERT: {
        label: "در انتظار بررسی کارشناس",
        color: "#d97706",
        bgColor: "rgba(217, 119, 6, 0.1)",
        borderColor: "rgba(217, 119, 6, 0.25)",
        icon: <HourglassEmptyRounded sx={{ fontSize: 16 }} />,
    },
    PROCESSING: {
        label: "در حال پیگیری",
        color: "#6366f1",
        bgColor: "rgba(99, 102, 241, 0.1)",
        borderColor: "rgba(99, 102, 241, 0.25)",
        icon: <AccessTimeRounded sx={{ fontSize: 16 }} />,
    },
    COMPLETE: {
        label: "پایان‌یافته",
        color: "#059669",
        bgColor: "rgba(5, 150, 105, 0.1)",
        borderColor: "rgba(5, 150, 105, 0.25)",
        icon: <CheckCircleRounded sx={{ fontSize: 16 }} />,
    },
    CANCEL: {
        label: "لغو شده",
        color: "#dc2626",
        bgColor: "rgba(220, 38, 38, 0.1)",
        borderColor: "rgba(220, 38, 38, 0.25)",
        icon: <CancelRounded sx={{ fontSize: 16 }} />,
    },
};

const SupportDetail = () => {
    const theme = useTheme();
    const error = useContext(ErrorContext);
    const { supportId } = useParams();

    const [supportDetail, setSupportDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [closing, setClosing] = useState(false);

    const messagesEndRef = useRef(null);

    useEffect(() => {
        setReadMessages();
    }, [supportId]);

    useEffect(() => {
        if (supportDetail) {
            scrollToBottom();
        }
    }, [supportDetail?.Messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const setReadMessages = () => {
        Support_setMessagesRead({ id: supportId })
            .catch(() => {})
            .finally(() => {
                getSupportDetail();
            });
    };

    const getSupportDetail = () => {
        setLoading(true);
        Support_getById({ id: supportId })
            .then(result => {
                setSupportDetail(result?.data?.Data);
            })
            .catch(e => {
                const msg = e?.response?.data?.Message || "خطا در دریافت اطلاعات تیکت";
                error.showError({ message: msg });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        const trimmed = replyText.trim();
        if (!trimmed) {
            error.showError({ message: "لطفاً متن پیام را وارد نمایید." });
            return;
        }

        setSending(true);
        try {
            await Support_addMessage({
                SupportId: supportId,
                Status: "AWAITING_EXPERT",
                Message: trimmed,
                IsRead: true,
            });
            setReplyText('');
            getSupportDetail();
        } catch (err) {
            const msg = err?.response?.data?.Message || "خطا در ارسال پیام. لطفاً دوباره تلاش کنید.";
            error.showError({ message: msg });
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const handleCloseSupport = async () => {
        setClosing(true);
        try {
            await Support_addMessage({
                SupportId: supportId,
                Status: "CANCEL",
                Message: "تیکت توسط مدیر سازمان بسته شد.",
                IsRead: true,
            });
            setConfirmCloseOpen(false);
            getSupportDetail();
        } catch (err) {
            const msg = err?.response?.data?.Message || "خطا در بستن تیکت";
            error.showError({ message: msg });
        } finally {
            setClosing(false);
        }
    };

    const isClosed = supportDetail?.Status === "COMPLETE" || supportDetail?.Status === "CANCEL";
    const statusConfig = STATUS_CONFIG[supportDetail?.Status] || {
        label: SupportStatus[supportDetail?.Status] || supportDetail?.Status,
        color: '#64748b',
        bgColor: 'rgba(100, 116, 139, 0.1)',
        borderColor: 'rgba(100, 116, 139, 0.2)',
        icon: null,
    };

    const formatPersianDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('fa-IR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    if (loading && !supportDetail) {
        return (
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={42} thickness={4} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        در حال دریافت جزئیات تیکت...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <title>{supportDetail ? `${supportDetail.Title} | تیکت #${supportDetail.Id}` : 'جزئیات پشتیبانی'}</title>

            {/* Header Card */}
            <_SupportHeader
                supportDetail={supportDetail}
                statusConfig={statusConfig}
                isClosed={isClosed}
                onOpenCloseDialog={() => setConfirmCloseOpen(true)}
                formatPersianDate={formatPersianDate}
            />

            {/* Conversation Messages Thread */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    p: { xs: 2, sm: 3 },
                    mb: 3,
                    minHeight: 380,
                }}
            >
                {supportDetail?.Messages && supportDetail.Messages.length > 0 ? (
                    <Box>
                        {supportDetail.Messages.map(item => (
                            <_SupportMessageItem key={item.Id || item.id} item={item} />
                        ))}
                        <div ref={messagesEndRef} />
                    </Box>
                ) : (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            هنوز پیامی در این تیکت ثبت نشده است.
                        </Typography>
                    </Box>
                )}
            </Card>

            {/* Reply Input Box or Closed Notice */}
            <_SupportReplyBox
                isClosed={isClosed}
                statusConfig={statusConfig}
                replyText={replyText}
                setReplyText={setReplyText}
                handleKeyDown={handleKeyDown}
                handleSendMessage={handleSendMessage}
                sending={sending}
            />

            {/* Confirm Close Dialog */}
            <_SupportCloseDialog
                open={confirmCloseOpen}
                onClose={() => setConfirmCloseOpen(false)}
                closing={closing}
                onConfirmClose={handleCloseSupport}
            />
        </Container>
    );
};

export default SupportDetail;
